<template>
    <transition name="fade">
        <suspense @resolve="isInitialized = true">
            <AppContent>
                <router-view v-slot="{ Component: Region }">
                    <component :is="Region">
                        <router-view v-slot="{ Component, route }">
                            <transition
                                :mode="transitionMode"
                                :name="transitionName"
                            >
                                <suspense
                                    suspensible
                                    @fallback="chunkLoadCompleted"
                                    @pending="chunkLoadStarted"
                                    @resolve="chunkLoadCompleted"
                                >
                                    <component
                                        :is="Component"
                                        :key="getControllerKey(route)"
                                        v-qa.hydrated="'hydrated'"
                                        :data-qa-layout="'controller'"
                                    />
                                </suspense>
                            </transition>
                        </router-view>
                    </component>
                </router-view>
            </AppContent>

            <template #fallback>
                <SplashScreen
                    :key="'loader'"
                    class="loader"
                />
            </template>
        </suspense>
    </transition>
</template>

<script lang="ts" setup>
// Line 2: Transition between fallback and default slots of suspense
// Line 3: Root suspense handles init
// Line 5: Root route will always load Region.vue
// Line 8: Transition between routes
// Line 12: Nested suspense handles async views, note the 'suspensible' arg
// Line 18: To disable transition between two given nested routes, use the same meta.name for both
import { provide, ref, watch, onErrorCaptured } from 'vue'
import { storeToRefs } from 'pinia'

import { useRouterTransition } from './composables/app/router-transitions/useRouterTransition'
import { useScrollPositionResolverStore } from './stores/app/lazyLoader'
import { getWindow } from '@/helpers/globals.ts'

import AppContent from './AppContent.vue'
import SplashScreen from './components/ui/SplashScreen.vue'
import { initialScroll, ROUTE_PAGE } from '@/router/index.ts'
import type { RouteLocationNormalizedLoadedGeneric } from 'vue-router'

const window = getWindow()
const { transitionMode, transitionName } = useRouterTransition()

const { isLazyLoadInProgress } = storeToRefs(useScrollPositionResolverStore())

const chunkLoadStarted = () => {
    isLazyLoadInProgress.value = true
}
const chunkLoadCompleted = () => {
    isLazyLoadInProgress.value = false
}

function getControllerKey(route: RouteLocationNormalizedLoadedGeneric): string {
    if (route.meta?.['name']) {
        return route.meta.name
    }

    if (route.name === ROUTE_PAGE) {
        // !!! VERY IMPORTANT
        // This resolved the CLS issues on Page.vue
        // Basically, vue does not wait for async components the second time around a page has to load
        // In case you navigate between two content pages
        // Your data is there, but the component code loads asynchronously, so the content does not show
        // Until the next JS event loop cycle.
        // So, by using a different key on each page, we force vue to re-render a new instance of Page.vue
        // Thus reusing the logic of <suspense /> to await the dynamic imports
        // But also maintaining lazy loading of CMS components to keep bundle size slim
        // The hash is excluded so fragment changes (e.g. gallery slides) don't re-render the page
        return route.fullPath.replace(/#.*$/, '')
    }

    return ''
}

watch(isLazyLoadInProgress, (newVal) => {
    if (!newVal) {
        if (window) {
            window.scrollTo({ ...initialScroll, behavior: 'instant' })
        }
    }
})

onErrorCaptured((error, target) => {
    // eslint-disable-next-line no-console
    console.log('Captured a Vue.JS Error', error, target)
})

const isInitialized = ref(false)

provide('isInitialized', isInitialized)
</script>

<style
    lang="scss"
    src="@/composables/app/router-transitions/transitions.scss"
></style>

<style lang="scss" src="./styles/vendor/reset.scss"></style>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.45s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
